import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getJson, postJson } from '@/api/client'
import type { LiveTestConversationsPage, LiveTestImportResult, LiveTestStats } from '@/api/types'
import { Badge } from '@/ui/primitives'
import { Button } from '@/ui/Button'
import { toast } from '@/ui/Toast'

const PAGE_SIZE = 25
const CATEGORIES = [null, 'beauty', 'order_status', 'complaint', 'pricing', 'general'] as const

const CAT_COLORS: Record<string, string> = {
  beauty: '#6A8D6A',
  order_status: '#C4A35A',
  complaint: '#C4705A',
  pricing: '#7A6A8A',
  product_availability: '#5A8A8A',
  store_info: '#8A7A5A',
  how_to_use: '#5A6A8A',
  social_media: '#8A5A6A',
  general: '#A09888',
}

function catLabel(cat: string | null): string {
  if (!cat) return '?'
  const m: Record<string, string> = {
    beauty: 'Beauty', order_status: 'Order', complaint: 'Complaint',
    pricing: 'Pricing', product_availability: 'Avail.', store_info: 'Store',
    how_to_use: 'How-to', social_media: 'Social', general: 'General',
  }
  return m[cat] || cat
}

function catBadge(cat: string | null): 'default' | 'success' | 'warning' | 'danger' {
  if (cat === 'complaint') return 'danger'
  if (cat === 'order_status') return 'warning'
  if (cat === 'beauty') return 'success'
  return 'default'
}

function formatSpan(first: string | null, last: string | null): string {
  if (!first) return ''
  const f = new Date(first)
  if (!last) return f.toLocaleDateString()
  const l = new Date(last)
  if (f.toDateString() === l.toDateString()) {
    return `${f.toLocaleDateString()} ${f.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${l.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }
  return `${f.toLocaleDateString()} – ${l.toLocaleDateString()}`
}

interface LiveTestDashboardProps {
  title?: string
  breadcrumb?: string
  apiBase?: string
  routeBase?: string
  emptyText?: string
}

export function LiveTestDashboard({
  title = 'Live Test',
  breadcrumb = 'Admin / Live Test',
  apiBase = '/v1/admin/live-test',
  routeBase = '/admin/live-test',
  emptyText = 'No conversations yet. Click Import to load data from the remote export.',
}: LiveTestDashboardProps) {
  const navigate = useNavigate()
  const [page, setPage] = useState<LiveTestConversationsPage | null>(null)
  const [stats, setStats] = useState<LiveTestStats | null>(null)
  const [filterCategory, setFilterCategory] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void loadAll() }, [filterCategory, search, currentPage])

  async function loadAll() {
    try {
      const params = new URLSearchParams()
      params.set('offset', String(currentPage * PAGE_SIZE))
      params.set('limit', String(PAGE_SIZE))
      if (filterCategory) params.set('category', filterCategory)
      if (search) params.set('search', search)

      const [pageData, statsRow] = await Promise.all([
        getJson<LiveTestConversationsPage>(`${apiBase}/conversations?${params}`),
        getJson<LiveTestStats>(`${apiBase}/stats`),
      ])
      setPage(pageData)
      setStats(statsRow)
      setError(null)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleImport() {
    setImporting(true)
    setError(null)
    try {
      const result = await postJson<LiveTestImportResult>(`${apiBase}/import`, {})
      toast.success(`Imported ${result.new_conversations} conversations (${result.new_turns} turns)`)
      setCurrentPage(0)
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setImporting(false) }
  }

  function handleSearch() {
    setSearch(searchInput)
    setCurrentPage(0)
  }

  const totalPages = page ? Math.ceil(page.total / PAGE_SIZE) : 0
  const conversations = page?.conversations || []
  const catBreakdown = stats?.category_breakdown || {}
  const hasAnalytics = Object.keys(catBreakdown).length > 0

  return (
    <div className="px-8 lg:px-14 py-12 h-full overflow-y-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 font-sans text-xs text-mute mb-3">
          <span className="w-1.5 h-1.5 bg-leaf" />
          <span>{breadcrumb}</span>
        </div>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-4xl font-medium text-cream tracking-tight leading-[1.05]">
              {title}
            </h1>
            {stats && (
              <p className="text-sm text-mute mt-2">
                {stats.total_conversations} conversations · {stats.total_turns} turns
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" loading={importing} onClick={handleImport}>Import</Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 font-display italic text-sm border-l-[3px] border-ember text-ember pl-4 py-2 bg-ember/5">
          {error}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="border border-haze/30 bg-ink-raised p-4">
            <div className="text-[10px] text-mute uppercase tracking-wider mb-1">Conversations</div>
            <div className="font-display text-2xl text-cream">{stats.total_conversations}</div>
          </div>
          <div className="border border-haze/30 bg-ink-raised p-4">
            <div className="text-[10px] text-mute uppercase tracking-wider mb-1">Turns</div>
            <div className="font-display text-2xl text-cream">{stats.total_turns}</div>
          </div>
          <div className="border border-haze/30 bg-ink-raised p-4">
            <div className="text-[10px] text-mute uppercase tracking-wider mb-1">Categories</div>
            <div className="font-display text-2xl text-cream">{Object.keys(catBreakdown).length}</div>
          </div>
        </div>
      )}

      {hasAnalytics && stats && (
        <div className="border border-haze/30 bg-ink-raised p-6 mb-10">
          <div className="text-xs text-mute uppercase tracking-wider mb-4">Category Distribution</div>
          <div className="flex flex-col-reverse h-48 mb-3" style={{ gap: 0 }}>
            {Object.entries(catBreakdown)
              .sort(([, a], [, b]) => b - a)
              .map(([cat, count]) => {
                const pct = (count / stats.total_conversations) * 100
                const color = CAT_COLORS[cat] || '#A09888'
                if (pct < 0.5) return null
                return (
                  <div
                    key={cat}
                    className="flex items-center px-2 text-[10px] text-cream transition-all duration-300 min-h-[14px]"
                    style={{
                      height: `${Math.max(pct * 0.48, 1)}rem`,
                      backgroundColor: color,
                      opacity: 0.85,
                      minWidth: 0,
                    }}
                    title={`${catLabel(cat)}: ${count}`}
                  >
                    <span className="truncate">{catLabel(cat)}</span>
                    <span className="ml-auto flex-shrink-0">{count}</span>
                  </div>
                )
              })}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
            {Object.entries(catBreakdown)
              .sort(([, a], [, b]) => b - a)
              .map(([cat, count]) => (
                <div key={cat} className="flex items-center gap-1.5 text-[10px]">
                  <span className="w-2 h-2 flex-shrink-0" style={{ backgroundColor: CAT_COLORS[cat] || '#A09888' }} />
                  <span className="text-mute">{catLabel(cat)}</span>
                  <span className="text-cream/70">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-xs text-mute uppercase tracking-wider">Conversations</span>
          <div className="flex gap-1.5 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c ?? 'all'}
                onClick={() => { setFilterCategory(c); setCurrentPage(0) }}
                className={`px-2.5 py-0.5 text-[10px] transition-colors ${
                  filterCategory === c ? 'bg-ember text-ink' : 'bg-ink-raised text-mute hover:text-cream'
                }`}
              >{c ? catLabel(c) : 'all'}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
            placeholder="Search customer..."
            className="bg-ink-raised border border-haze/30 px-3 py-1 text-xs text-cream placeholder-mute/50 outline-none focus:border-haze/60"
          />
          <button
            onClick={handleSearch}
            className="px-2.5 py-1 text-[10px] border border-haze/40 text-mute hover:text-cream hover:border-haze/60 transition-colors"
          >Search</button>
        </div>
      </div>

      <div className="border border-haze/30 bg-ink-raised overflow-hidden">
        <div className="divide-y divide-haze/20">
          {conversations.length === 0 ? (
            <div className="px-5 py-12 text-center text-mute text-xs">
              {emptyText}
            </div>
          ) : (
            conversations.map((conv) => (
              <div key={conv.id}>
                <button
                  onClick={() => navigate(`${routeBase}/${conv.id}`)}
                  className="w-full px-4 py-2.5 text-left transition-all duration-200 hover:bg-ink/30"
                >
                  <div className="grid grid-cols-[1fr_80px_100px] gap-3 items-center">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-sm text-cream font-display truncate">
                        {conv.customer_name || `User #${conv.remote_from_id}`}
                      </span>
                      {conv.primary_category && (
                        <Badge variant={catBadge(conv.primary_category)}>{catLabel(conv.primary_category)}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-mute text-center">
                      {conv.total_human_messages} turns
                    </div>
                    <div className="text-[10px] text-mute/50 text-right">
                      {conv.first_message_ts ? formatSpan(conv.first_message_ts, conv.last_message_ts) : ''}
                    </div>
                  </div>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-5">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="px-2.5 py-1 text-[10px] border border-haze/40 text-mute hover:text-cream hover:border-haze/60 disabled:opacity-30 transition-colors"
          >← Prev</button>
          {(() => {
            const pages: (number | '...')[] = []
            const add = (p: number) => { if (!pages.includes(p)) pages.push(p) }
            if (totalPages <= 7) {
              for (let i = 0; i < totalPages; i++) add(i)
            } else {
              add(0)
              const start = Math.max(1, currentPage - 1)
              const end = Math.min(totalPages - 2, currentPage + 1)
              if (start > 1) pages.push('...')
              for (let i = start; i <= end; i++) add(i)
              if (end < totalPages - 2) pages.push('...')
              add(totalPages - 1)
            }
            return pages.map((p, i) =>
              p === '...' ? (
                <span key={`e${i}`} className="text-[10px] text-mute px-1">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`px-2.5 py-1 text-[10px] transition-colors ${
                    currentPage === p
                      ? 'bg-ember text-ink'
                      : 'border border-haze/40 text-mute hover:text-cream hover:border-haze/60'
                  }`}
                >{p + 1}</button>
              )
            )
          })()}
          <button
            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage >= totalPages - 1}
            className="px-2.5 py-1 text-[10px] border border-haze/40 text-mute hover:text-cream hover:border-haze/60 disabled:opacity-30 transition-colors"
          >Next →</button>
        </div>
      )}

      {page && (
        <div className="text-center text-[10px] text-mute mt-3">
          {page.offset + 1}–{Math.min(page.offset + page.limit, page.total)} of {page.total}
        </div>
      )}
    </div>
  )
}

export function RealChatReplayDashboard() {
  return (
    <LiveTestDashboard
      title="Real Chat Replay"
      breadcrumb="Admin / Real Chat Replay"
      apiBase="/v1/admin/real-chat-replay"
      routeBase="/admin/real-chat-replay"
      emptyText="No replay conversations yet. Pull a local export with scripts/pull_live_test.py, then import it here."
    />
  )
}
